import {
    RoomAccessTokenRequestParams,
    RoomCollection,
    buildTwilioRoomAccessTokenParams,
} from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { RoomGuest, RoomGuestListType } from "@lifeat/shared/data-models/Rooms/RoomGuest";
import { RoomModerationInfoCollection } from "@lifeat/shared/data-models/Rooms/RoomModerationTypes";
import { UserCollection, UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { allRoomIdsOwnedByUser, assembleRoomGuest } from "@lifeat/shared/rooms/RoomUtil";
import { ROOMS_NODE } from "@lifeat/shared/rtdb-nodes/RtdbNodes";
import { db, functions } from "@firebase-local/firebase";
import { ROOMS_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import { logError } from "@service/ConsoleLoggerService";
import { DatabaseService } from "@service/DatabaseService";
import { getPublicUserInfo } from "@user/service/UserService";
import { LAST_VISITED_ROOMS_LIST_SIZE } from "./RoomConstants";
import { RoomDisplayInfoList } from "./RoomTypes";
import { RoomPublicUserInfoList } from "./room-chat/RoomChatTypes";

/**
 * Initialize a record in the /rooms node for a user if they do not have a
 * Room ID associated with their account.
 */
export const initUserRoomsData = () => {
    functions
        .httpsCallable(ROOMS_FUNCTIONS.InitUserRoomsData)()
        .catch((error) => {
            logError(`There was an error generating Room Data for user. Error: ${JSON.stringify(error)}`);
        });
};

/**
 * Get a ref to the room locked field.
 * @param roomId
 */
export const roomLockedRef = (roomId: string) => {
    return db.ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.IS_PRIVATE}`);
};

/**
 * Check if the room is locked by the host and perform an action.
 * @param roomId
 * @param callback
 */
export const checkIfRoomLocked = (roomId: string, callback: (lockStatus: boolean) => void): void => {
    roomLockedRef(roomId)
        .once("value", (snapshot) => {
            if (snapshot.exists()) {
                return snapshot.val();
            }
        })
        .then((snapshot) => {
            const locked: boolean = snapshot.val();
            callback(locked);
        });
};

/**
 * Get a ref to the room name field.
 * @param roomId
 */
export const roomNameRef = (roomId: string) => {
    return db.ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.ROOM_NAME}`);
};

/**
 * Listens to the name of the room for changes.
 * @param roomId
 * @param callback
 */
export const listenToRoomName = (roomId: string, callback: (roomName: string) => void) => {
    return roomNameRef(roomId).on("value", (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.val());
        }
    });
};

/**
 * Update the DB record for the Room to indicate that the host is owned by a
 * user on the Pro tier. This is to handle the duration of video calling for
 * free guests.
 * @param roomId
 * @param userData
 */
export const setIsRoomHostProUser = async (roomId: string, userData: UserEntity) => {
    if (userData?.roomsData?.personalRoomId === roomId) {
        const isPro = userData.proUserInfo?.isProUser ?? false;
        await db.ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.IS_ROOM_HOST_PRO}`).set(isPro);
    }
};

/**
 * Set the space ID that the Room is on (controlled by the host).
 * @param roomId
 * @param spaceId
 */
export const setRoomSpaceId = (roomId: string, spaceId: string) => {
    const ROOM_SPACE_ID_PATH = `/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.SPACE_ID}`;
    const updates: { [id: string]: string } = {};

    updates[ROOM_SPACE_ID_PATH] = spaceId;
    db.ref()
        .update(updates)
        .catch((e) => {
            logError(`ERROR: ${JSON.stringify(e)}`);
        });
};

/**
 * Add a guest to the Room so that they are visible to all other users in the Room.
 * @param roomId Room being joined by Guest.
 * @param newGuest Guest's details.
 */
const addGuestToRoom = async (roomId: string, newGuest: RoomGuest) => {
    // Add the user to the Room's list of members if not already there.
    const database = DatabaseService();
    const memberInfo = await database.get(
        RoomModerationInfoCollection.at(roomId).at("allRoomMembersInfo").at("roomMembers").at(newGuest.userId).node,
    );

    if (!memberInfo) {
        functions
            .httpsCallable(ROOMS_FUNCTIONS.AddUserToMemberList)({ roomId })
            .catch((e) => {
                logError(`Failed to add user to room member list. Error: ${JSON.stringify(e)}`);
            });
    }

    // Then add the user to the Room's list of guests to indicate they are online.
    return db.ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.GUESTS}/${newGuest.userId}`).set(newGuest);
};

/**
 * When the user leaves the Room (disconnects), remove them from the DB, so they
 * are no longer visible to users still in the Room.
 * @param roomId ID of the Room.
 * @param guestId User ID of a guest.
 */
async function scheduleGuestRemovalOnDisconnect(roomId: string, guestId: string) {
    const onDisconnectRef = db
        .ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.GUESTS}`)
        .child(guestId)
        .onDisconnect();
    await onDisconnectRef.cancel();
    await onDisconnectRef.set(null);
}

/**
 * As the host, remove all guests in the Room and set the Room as not open so
 * guests cannot re-join.
 * @param {string} roomId The room ID to update in DB (/rooms node)
 * @param {RoomGuest} hostGuestInfo Host's user information, because the host
 * will still be in the room and should be listed in the guests field.
 */
export const removeAllGuestsFromRoom = (roomId: string, hostGuestInfo: RoomGuest) => {
    let updates: { [id: string]: boolean | null | RoomGuest } = {};
    // First, clear out the guests data and set the room to be closed.
    updates[ROOMS_NODE.children.GUESTS] = null;
    return db
        .ref(`/${ROOMS_NODE.name}/${roomId}`)
        .update(updates)
        .then(() => {
            // Once the room is cleared out, then update the guests with the host's
            // information, as they should remain in the room.
            updates = {};
            updates[`${ROOMS_NODE.children.GUESTS}/${hostGuestInfo.userId}`] = hostGuestInfo;
            return db.ref(`/${ROOMS_NODE.name}/${roomId}`).update(updates);
        });
};

/**
 * Get a DB ref to the guests field, at `/rooms/{roomId}/guests`.
 * @param roomId The Room ID.
 */
export const roomGuestsRef = (roomId: string) => {
    const ROOM_GUESTS_PATH = `/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.GUESTS}`;
    return db.ref(ROOM_GUESTS_PATH);
};

/**
 * Create a listener for the guests in a Room.
 * @param roomId The Room ID.
 * @param callback Action to perform when the data at the node changes.
 */
export const roomGuestsListener = (roomId: string, callback: (newGuests: { [id: string]: RoomGuest }) => void) => {
    return roomGuestsRef(roomId).on("value", (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.val());
        }
    });
};

export const getIsHostInfoDisplayedRef = (roomId: string) => {
    return db.ref(RoomCollection.at(roomId).at("isHostInfoDisplayed").node.path);
};

/**
 * Update the Room node in DB to indicate if guests should be synced to the
 * Room host's space.
 * @param {string} roomId The Room ID to update in /rooms
 * @param {boolean} status True if guests should be synced. False if not.
 */
export const setIsRoomSpaceSyncedStatus = (roomId: string, status: boolean) => {
    return db.ref(`/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.IS_SPACE_SYNCED}`).set(status);
};

/**
 * Update the Room node in DB to indicate if host's info should be displayed in room
 * @param {string} roomId The Room ID to update in /rooms
 * @param {boolean} isHostInfoDisplayed If host info should be displayed
 */
export const updateIsHostInfoDisplayed = (roomId: string, isHostInfoDisplayed: boolean) => {
    return getIsHostInfoDisplayedRef(roomId).set(isHostInfoDisplayed);
};

/**
 * Create a DB ref to the room space field at `/rooms/{roomId}/spaceId`
 * @param {string} roomId The Room ID
 */
export const roomSpaceRef = (roomId: string) => {
    const ROOM_SPACE_ID_PATH = `/${ROOMS_NODE.name}/${roomId}/${ROOMS_NODE.children.SPACE_ID}`;
    return db.ref(ROOM_SPACE_ID_PATH);
};

/**
 * Attach a listener to the room space field at `/rooms/{roomId}/spaceId`
 * @param {string} roomId The Room ID
 * @param callback Action to perform when the value in the DB changes.
 */
export const listenToRoomSpace = (roomId: string, callback: (spaceId: string) => void) => {
    return roomSpaceRef(roomId).on("value", (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.val());
        }
    });
};

const getLastVisitedRoomsListNode = (uid: string) => {
    return UserCollection.at(uid).at("roomsData").at("lastVisitedRooms").node;
};

/**
 * Update `/users/{uid}/roomsData/lastVisitedRooms` with the last three visited rooms
 * @param {string} uid User ID
 * @param {string} roomId Room ID to update lastVisitedRooms with
 */
export const updateLastVisitedRooms = async (uid: string, roomId: string) => {
    const database = DatabaseService();
    const lastVisitedRoomsNode = getLastVisitedRoomsListNode(uid);
    const currLastVisitedRooms = await database.get(lastVisitedRoomsNode);

    const { shouldUpdate, updatedLastVisitedRooms } = determineLastVisitedRoomListChanges(
        currLastVisitedRooms ?? [],
        roomId,
    );

    if (shouldUpdate) {
        database.set(lastVisitedRoomsNode, updatedLastVisitedRooms).catch((error) => {
            logError("Error updating last visited rooms: " + getErrorMessage(error));
        });
    }
};

/**
 * Determine updated list for lastVisitedRooms and if the DB value needs to be updated
 * @param currLastVisitedRooms Current list of last visited rooms from DB
 * @param roomId Newly visited room to add
 */
export const determineLastVisitedRoomListChanges = (
    currLastVisitedRooms: string[],
    roomId: string,
): { shouldUpdate: boolean; updatedLastVisitedRooms: string[] } => {
    // if list already contains roomId, remove it (so it can be added to the end)
    let updatedLastVisitedRooms = currLastVisitedRooms.filter((existingRoomId: string) => existingRoomId !== roomId);

    updatedLastVisitedRooms.push(roomId);

    // if list contains more than N room Ids, only get the last N items
    updatedLastVisitedRooms = updatedLastVisitedRooms.slice(-LAST_VISITED_ROOMS_LIST_SIZE);

    const shouldUpdate = JSON.stringify(currLastVisitedRooms) !== JSON.stringify(updatedLastVisitedRooms);
    return { shouldUpdate: shouldUpdate, updatedLastVisitedRooms: updatedLastVisitedRooms };
};

/**
 * Fetch room names for a given list of roomIds
 * @param roomIds
 */
const getRoomDisplayInfoForRoomIds = async (roomIds: string[]): Promise<RoomDisplayInfoList> => {
    let roomDisplayInfoList: RoomDisplayInfoList = [];

    for (const roomId of roomIds) {
        const roomName = await roomNameRef(roomId).get();
        if (roomName.val()) {
            roomDisplayInfoList.push({ id: roomId, name: roomName.val() });
        }
    }

    return roomDisplayInfoList;
};

/**
 * Get roomID and room names for user's last visited rooms
 * @param uid User Id
 */
export const getLastVisitedRoomsDisplayNames = async (uid: string): Promise<RoomDisplayInfoList> => {
    const database = DatabaseService();
    const lastVisitedRoomsArrSnapshot = await database.get(getLastVisitedRoomsListNode(uid));

    return await getRoomDisplayInfoForRoomIds(lastVisitedRoomsArrSnapshot ?? []);
};

/**
 * Get roomId and room names for all user's rooms
 * @param userData
 */
export const getAllPersonalRoomNames = async (userData: UserEntity): Promise<RoomDisplayInfoList> => {
    const personalRoomIds = allRoomIdsOwnedByUser(userData);

    return await getRoomDisplayInfoForRoomIds(personalRoomIds);
};

/**
 * Generates an access token to be used in joining a Twilio video call.
 * @param uid Auth user ID
 * @param userEmail Email to be used as unique identifier for user in Twilio's system.
 * @param roomId LifeAt Room ID that the token will be used to join.
 * @returns {string} The Twilio access token.
 */
export const generateTwilioRoomAccessToken = async (
    uid: string,
    userEmail: string,
    roomId: string,
): Promise<string> => {
    const requestTokenData: RoomAccessTokenRequestParams = buildTwilioRoomAccessTokenParams(userEmail, roomId);

    return await functions
        .httpsCallable(ROOMS_FUNCTIONS.GenerateTwilioRoomAccessToken)(requestTokenData)
        .then((res) => {
            return res.data;
        })
        .catch((e) => {
            const errMsg = `Failed to generate access token for video call.\n\n${JSON.stringify(e)}`;
            alert(`${errMsg}\n\nPlease contact LifeAt Support with a screenshot of this error.`);
            throw e;
        });
};

export async function handleRoomEnter(roomId: string, uid: string, userData: UserEntity) {
    const guest = assembleRoomGuest(uid, roomId, userData);
    await scheduleGuestRemovalOnDisconnect(roomId, uid);
    await addGuestToRoom(roomId, guest);
}

/**
 * Returns a list of Message Author info given a list of uids
 * @param uids Array of uids
 * @param roomOwnerUid Uid of room owner, to indicate if user is host
 * @param roomGuests Room guests info
 */
export const getUsersPublicInfoArray = async (
    uids: string[],
    roomOwnerUid: string,
    roomGuests?: RoomGuestListType | undefined | null,
): Promise<RoomPublicUserInfoList> => {
    let publicUserInfos: RoomPublicUserInfoList = {};

    for (const uid of uids) {
        const isUserHost = uid === roomOwnerUid;
        if (roomGuests?.[uid]) {
            publicUserInfos[uid] = {
                uid: uid,
                userDisplayName: roomGuests[uid]?.userDisplayName,
                userAviUrl: roomGuests[uid]?.userAviUrl || undefined,
                isHost: isUserHost,
            };
        } else {
            const publicUserInfo = await getPublicUserInfo(uid);
            publicUserInfos[uid] = { ...publicUserInfo, isHost: isUserHost };
        }
    }

    return publicUserInfos;
};
